/**
 * SEO Configuration for WIH Media
 *
 * This file contains all SEO metadata for each page
 * Update these values when page content changes
 */

import { SEOProps } from "../components/SEO";

const SITE_URL = "https://wihmedia.com";

// ============================================
// Page-specific SEO configurations
// ============================================

export const seoConfig: Record<string, SEOProps> = {
  home: {
    title: "Professional Real Estate Photography in Indian River County",
    description:
      "WIH Media provides professional real estate photography, FAA-certified aerial drone photography, virtual tours, and video services in Vero Beach and Indian River County, Florida. Book your session today!",
    keywords: [
      "real estate photography Vero Beach",
      "drone photography Indian River County",
      "property photography Florida",
      "aerial photography Vero Beach",
      "real estate marketing",
      "professional property photos",
      "FAA Part 107 certified",
      "real estate video tours",
      "virtual tours Florida",
    ],
    canonicalPath: "/",
    ogType: "website",
  },

  services: {
    title: "Real Estate Photography Services",
    description:
      "Comprehensive real estate photography services including professional interior/exterior photos, FAA-certified drone photography, 4K video tours, 360° virtual tours, and twilight photography. Serving Vero Beach and Indian River County.",
    keywords: [
      "real estate photography services",
      "drone photography services",
      "virtual tour services",
      "property video tour",
      "twilight photography",
      "360 virtual tour",
      "HDR photography",
      "real estate marketing services",
      "Vero Beach photographer",
    ],
    canonicalPath: "/services",
    ogType: "website",
  },

  portfolio: {
    title: "Portfolio - Real Estate Photography Examples",
    description:
      "Browse our portfolio of stunning real estate photography, aerial drone shots, and video tours. See examples of our work across Indian River County properties.",
    keywords: [
      "real estate photography portfolio",
      "property photography examples",
      "drone photography samples",
      "real estate photo gallery",
      "Vero Beach property photos",
      "Florida real estate photography",
    ],
    canonicalPath: "/portfolio",
    ogType: "website",
  },

  booking: {
    title: "Book a Photography Session",
    description:
      "Schedule your professional real estate photography session with WIH Media. Easy online booking for photography, drone, and video services in Indian River County.",
    keywords: [
      "book real estate photographer",
      "schedule photography session",
      "property photography booking",
      "drone photography appointment",
      "Vero Beach photographer booking",
    ],
    canonicalPath: "/booking",
    ogType: "website",
  },

  contact: {
    title: "Contact Us",
    description:
      "Get in touch with WIH Media for professional real estate photography services in Indian River County. Request a quote, ask questions, or discuss your project needs.",
    keywords: [
      "contact real estate photographer",
      "photography quote request",
      "Vero Beach photographer contact",
      "real estate photography inquiry",
    ],
    canonicalPath: "/contact",
    ogType: "website",
  },

  faq: {
    title: "Frequently Asked Questions",
    description:
      "Find answers to common questions about our real estate photography services, pricing, turnaround times, and booking process.",
    keywords: [
      "real estate photography FAQ",
      "property photography questions",
      "drone photography FAQ",
      "photography pricing questions",
    ],
    canonicalPath: "/faq",
    ogType: "website",
  },

  showroom: {
    title: "Virtual Showroom - Interactive Property Tours",
    description:
      "Explore our virtual showroom featuring interactive 360° tours and immersive property presentations. See how virtual tours can elevate your listings.",
    keywords: [
      "virtual showroom",
      "360 property tour",
      "interactive real estate tour",
      "virtual property walkthrough",
      "immersive property experience",
    ],
    canonicalPath: "/showroom",
    ogType: "website",
  },

  cart: {
    title: "Shopping Cart",
    description: "Complete your booking for professional real estate photography services.",
    noIndex: true, // Don't index cart pages
    canonicalPath: "/cart",
  },

  login: {
    title: "Sign In",
    description:
      "Sign in to your WIH Media account to manage bookings and access your deliverables.",
    noIndex: true,
    canonicalPath: "/login",
  },

  signup: {
    title: "Create Account",
    description: "Create a WIH Media account to book photography sessions and manage your orders.",
    noIndex: true,
    canonicalPath: "/signup",
  },
};

// ============================================
// Structured Data for the business
// ============================================

export const businessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}#business`,
  name: "WIH Media",
  image: `${SITE_URL}/logo.jpg`,
  url: SITE_URL,
  description:
    "Professional real estate photography, FAA Part 107 certified aerial drone photography, and marketing services in Indian River County, Florida.",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Vero Beach",
    addressRegion: "FL",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 27.6386,
    longitude: -80.3973,
  },
  areaServed: [
    {
      "@type": "City",
      name: "Vero Beach",
    },
    {
      "@type": "County",
      name: "Indian River County",
    },
    {
      "@type": "State",
      name: "Florida",
    },
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "18:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Saturday"],
      opens: "09:00",
      closes: "16:00",
    },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Photography Services",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Real Estate Photography",
          description:
            "Professional interior and exterior property photography with HDR processing",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Aerial Drone Photography",
          description: "FAA Part 107 certified drone photography and videography",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Video Tours",
          description: "Cinematic property video tours and walkthroughs",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "360° Virtual Tours",
          description: "Interactive 360-degree virtual property tours",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Twilight Photography",
          description: "Stunning dusk and twilight property photography",
        },
      },
    ],
  },
};

// ============================================
// Service-specific structured data
// ============================================

export const serviceSchemas = {
  realEstatePhotography: {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Real Estate Photography",
    description:
      "Professional interior and exterior property photography with HDR processing, perfect lighting, and fast turnaround.",
    provider: {
      "@type": "LocalBusiness",
      name: "WIH Media",
      url: SITE_URL,
    },
    areaServed: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: 27.6386,
        longitude: -80.3973,
      },
      geoRadius: "50 mi",
    },
    offers: {
      "@type": "Offer",
      priceSpecification: {
        "@type": "PriceSpecification",
        price: "150",
        priceCurrency: "USD",
        minPrice: "150",
      },
    },
  },

  aerialPhotography: {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Aerial Drone Photography",
    description:
      "FAA Part 107 certified aerial photography and videography. Stunning bird's-eye views of properties, neighborhoods, and amenities.",
    provider: {
      "@type": "LocalBusiness",
      name: "WIH Media",
      url: SITE_URL,
    },
    areaServed: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: 27.6386,
        longitude: -80.3973,
      },
      geoRadius: "50 mi",
    },
    offers: {
      "@type": "Offer",
      priceSpecification: {
        "@type": "PriceSpecification",
        price: "200",
        priceCurrency: "USD",
        minPrice: "200",
      },
    },
  },

  videoTours: {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Video Tours & Walkthroughs",
    description:
      "Professional cinematic video tours with gimbal-stabilized footage, background music, and social media optimization.",
    provider: {
      "@type": "LocalBusiness",
      name: "WIH Media",
      url: SITE_URL,
    },
    offers: {
      "@type": "Offer",
      priceSpecification: {
        "@type": "PriceSpecification",
        price: "300",
        priceCurrency: "USD",
        minPrice: "300",
      },
    },
  },

  virtualTours: {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "360° Virtual Tours",
    description:
      "Immersive 360-degree photography experiences with interactive navigation, mobile compatibility, and MLS embedding.",
    provider: {
      "@type": "LocalBusiness",
      name: "WIH Media",
      url: SITE_URL,
    },
    offers: {
      "@type": "Offer",
      priceSpecification: {
        "@type": "PriceSpecification",
        price: "250",
        priceCurrency: "USD",
        minPrice: "250",
      },
    },
  },
};

// ============================================
// FAQ structured data
// ============================================

export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How quickly can I get my photos?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Standard turnaround is 24-48 hours. Rush delivery is available for same-day or next-morning delivery.",
      },
    },
    {
      "@type": "Question",
      name: "What areas do you serve?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We primarily serve Vero Beach and Indian River County, Florida. We also cover surrounding areas within a 50-mile radius.",
      },
    },
    {
      "@type": "Question",
      name: "Are you FAA certified for drone photography?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, our drone pilots are FAA Part 107 certified, ensuring safe and legal aerial photography operations.",
      },
    },
    {
      "@type": "Question",
      name: "How long does a typical photo shoot take?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A standard real estate photo shoot takes 1-2 hours depending on the property size. Larger properties or those requiring drone work may take longer.",
      },
    },
    {
      "@type": "Question",
      name: "Do you offer virtual staging?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes, we offer virtual staging services to help showcase empty properties with digitally added furniture and decor.",
      },
    },
  ],
};

// ============================================
// Breadcrumb generator
// ============================================

export function generateBreadcrumbs(pageName: string, pagePath: string): object {
  const breadcrumbs = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: SITE_URL,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: pageName,
      item: `${SITE_URL}${pagePath}`,
    },
  ];

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs,
  };
}
